import type { ReactNode } from 'react';
import type { Permission, Scope } from '../../api/types/permissions';
import type { PrincipalIdentifierLocal } from '../../api/types/PlatformLocal';
import type {
    CreateApiUserRequestLocal,
    CreateApiUserResponseLocal,
    CreateTenantUserRequestLocal,
    TenantUserListItemLocal,
} from '../../api/types/TenantLocal';
import { useAuth } from '../../hooks/useAuth';
import { useMyTenantIdentifier } from '../../hooks/useMyTenantIdentifier';
import { useTenantIdentifier } from '../../hooks/useTenantIdentifier';
import { useMyTenantLevels, useTenantLevels } from '../../hooks/useTenantLevels';
import type { TenantLevelOption } from './levelColumns';
import { useTenantName } from '../../hooks/useTenantName';
import { useLabelResolver, type LabelResolver } from '../../hooks/useLabelResolver';
import { useTenantLabelResolver } from '../../hooks/useTenantLabelResolver';
import { organizationName } from '../../lib/scopeLabels';
import { displayNameOrShort } from '../../lib/displayName';
import { tenantApi } from '../../api/endpoints/tenant';
import { platformApi } from '../../api/endpoints/platform';
import { apiUserApi } from '../../api/endpoints/apiUsers';
import { UserRowActions } from '../tenant/UserRowActions';
import { PlatformTenantUserRowActions } from '../../pages/platform/tenants/users/PlatformTenantUserRowActions';
import type { RoleItem } from './useUserCreateFormCore';

/** Binding de API uniforme por scope: todas reciben el scopeRef como primer arg. */
export interface ScopedUserApi {
    listUsers: (scopeRef: string, params?: Record<string, unknown>) => Promise<unknown>;
    getUser: (scopeRef: string, cognitoSub: string) => Promise<unknown>;
    createUser: (scopeRef: string, body: unknown, opts?: unknown) => Promise<unknown>;
    updateUser: (scopeRef: string, cognitoSub: string, body: unknown) => Promise<unknown>;
    disableUser: (scopeRef: string, cognitoSub: string) => Promise<unknown>;
    enableUser: (scopeRef: string, cognitoSub: string) => Promise<unknown>;
    deleteUser: (scopeRef: string, cognitoSub: string) => Promise<unknown>;
    resetPassword: (scopeRef: string, cognitoSub: string) => Promise<unknown>;
    resetMfa: (scopeRef: string, cognitoSub: string) => Promise<unknown>;
    queryAudit: (scopeRef: string, params?: Record<string, unknown>) => Promise<unknown>;
}

export interface ScopeDescriptor {
    scope: Scope; // 'TENANT'
    permPrefix: 'tenant';
    /**
     * scopeRef del contexto. TENANT: el tenantId del actor (auth). PLATFORM (drill-down): `undefined`
     * — el scopeRef sale del PATH y el call-site lo pasa por prop (mismo patrón que `ScopedAuditTable`).
     */
    useScopeRef: () => string | undefined;
    /** Hook del identificador PRIMARIO del tenant del `scopeRef` (con qué dato inician sesión sus users). */
    useIdentifier: (scopeRef: string) => { principalIdentifier: PrincipalIdentifierLocal };
    /**
     * Niveles que define la aplicación del tenant del `scopeRef` (`{ level, displayName }`). La tabla
     * de usuarios arma UNA columna por nivel con su nombre real ("Grupos" en agents, "Sucursales" en
     * otro) en vez de hardcodear la etiqueta. Cada contexto lo resuelve por su vía: el tenant-admin
     * desde `/me/profile`, el drill-down platform desde `getTenant`. `[]` ⇒ sin columnas por nivel.
     */
    useLevels: (scopeRef: string) => { levels: TenantLevelOption[] };
    /** Catálogo de roles del tenant del `scopeRef` (alimenta el <select> de rol del alta). */
    listRoles: (scopeRef: string) => Promise<{ items: RoleItem[] }>;
    rolesQueryKey: (scopeRef: string) => readonly unknown[];
    /** Etiqueta readonly del destino TENANT del alta: el nombre de la organización. */
    useTenantDestLabel: (scopeRef: string) => string;
    /** Resolvedor del tenant del `scopeRef` (propio en tenant, el visto en platform). */
    useLabels: (scopeRef: string) => LabelResolver;
    api: ScopedUserApi;
    /** Alta de un sistema (API) en el tenant del `scopeRef`. El backend re-valida el permiso. */
    createApiUser: (
        scopeRef: string,
        body: CreateApiUserRequestLocal,
        opts: { idempotencyKey: string },
    ) => Promise<CreateApiUserResponseLocal>;
    queryKeyBase: (scopeRef: string) => readonly unknown[];
    /** Render del menú "⋮" por fila (cada scope inyecta su binding de API sobre el scopeRef). */
    renderRowActions: (args: { user: TenantUserListItemLocal; scopeRef: string }) => ReactNode;
    /** Ruta a la que navega el alta tras crear cuando NO hay `onDone` (solo el flujo con página propia). */
    defaultDoneRoute?: string;
    usersTable?: { showCreatedAt?: boolean };
}

/**
 * Subconjunto del descriptor que necesita `ScopedAuditTable` — solo scopeRef + queryKey + el binding
 * `queryAudit`. La auditoría no toca el resto del user-flow (identifier/roles/row-actions), así el flujo
 * platform (que no tiene esos conceptos) puede definir un descriptor de audit LEAN. `ScopeDescriptor`
 * lo satisface estructuralmente (es un superconjunto), así el audit del tenant sigue pasándose entero.
 */
export interface AuditScopeDescriptor {
    useScopeRef: () => string | undefined;
    queryKeyBase: (scopeRef: string) => readonly unknown[];
    api: Pick<ScopedUserApi, 'queryAudit'>;
}

/** Permisos derivados del descriptor — granularidad intacta, sin hardcodear en el motor. */
export function userPerm(d: ScopeDescriptor, action: string): Permission {
    return `${d.permPrefix}.user.${action}` as Permission;
}

export const TENANT_DESCRIPTOR: ScopeDescriptor = {
    scope: 'TENANT',
    permPrefix: 'tenant',
    // `user` puede ser null (sesión sin cargar); el `?.` lo cubre → string | undefined.
    useScopeRef: () => useAuth().user?.tenantId,
    // Contexto tenant-admin: el identificador del PROPIO tenant del actor (sin getTenant).
    useIdentifier: () => useMyTenantIdentifier(),
    // Contexto tenant-admin: los niveles del PROPIO tenant (de /me/profile — no hay getTenant aquí).
    useLevels: () => useMyTenantLevels(),
    listRoles: (tid) => tenantApi.listRoles(tid),
    rolesQueryKey: (tid) => ['tenant', tid, 'roles'],
    useTenantDestLabel: (tid) => displayNameOrShort(useTenantName(), tid),
    useLabels: () => useLabelResolver(),
    api: tenantApi as unknown as ScopedUserApi,
    createApiUser: (tid, body, opts) => apiUserApi.create(tid, body, opts),
    queryKeyBase: (tid) => ['tenant', tid],
    renderRowActions: ({ user, scopeRef }) => <UserRowActions user={user} tid={scopeRef} />,
    defaultDoneRoute: '/tenant/users',
    usersTable: { showCreatedAt: false },
};

/**
 * Adapter de `platformApi.*TenantUser` al binding uniforme `ScopedUserApi`. Los arrows resuelven la
 * llamada en CALL-TIME (no capturan la referencia al definir) → los `vi.spyOn(platformApi, …)` de los
 * tests interceptan igual. `scopeRef` = el tenantId del drill-down (path).
 */
const platformTenantScopedApi: ScopedUserApi = {
    listUsers: (ref, params) => platformApi.listTenantUsers(ref, params),
    getUser: (ref, sub) => platformApi.getTenantUser(ref, sub),
    createUser: (ref, body, opts) =>
        platformApi.createTenantUser(
            ref,
            body as CreateTenantUserRequestLocal,
            opts as Parameters<typeof platformApi.createTenantUser>[2],
        ),
    updateUser: (ref, sub, body) =>
        platformApi.updateTenantUser(
            ref,
            sub,
            body as Parameters<typeof platformApi.updateTenantUser>[2],
        ),
    disableUser: (ref, sub) => platformApi.disableTenantUser(ref, sub),
    enableUser: (ref, sub) => platformApi.enableTenantUser(ref, sub),
    deleteUser: (ref, sub) => platformApi.deleteTenantUser(ref, sub),
    resetPassword: (ref, sub) => platformApi.resetPasswordTenantUser(ref, sub),
    resetMfa: (ref, sub) => platformApi.resetMfaTenantUser(ref, sub),
    // El audit del drill-down no pasa por este descriptor; se mapea al query platform para satisfacer
    // el contrato sin duplicar tipos (no lo consume la tabla de usuarios).
    queryAudit: (_ref, params) => platformApi.queryAudit(params),
};

/**
 * Drill-down de un tenant desde el módulo Platform (`/platform/tenants/{tenantId}/users`). Mismo motor
 * que el flujo tenant, pero el scopeRef sale del PATH (no del actor), la data consume
 * `platformApi.*TenantUser`/`listTenantRoles`, el identificador se resuelve con `getTenant(tenantId)` y
 * las row-actions usan el binding platform. El permiso/scope del alta siguen siendo `tenant.user.*` /
 * TENANT (el platform admin los cubre por rank; el backend re-enforza).
 */
export const PLATFORM_TENANT_DESCRIPTOR: ScopeDescriptor = {
    scope: 'TENANT',
    permPrefix: 'tenant',
    // El scopeRef sale del PATH (prop), no del actor.
    useScopeRef: () => undefined,
    useIdentifier: (tenantId) => useTenantIdentifier(tenantId),
    // Drill-down: los niveles salen del detalle del tenant del PATH.
    useLevels: (tenantId) => useTenantLevels(tenantId),
    listRoles: (tenantId) => platformApi.listTenantRoles(tenantId),
    rolesQueryKey: (tenantId) => ['platform', 'tenants', tenantId, 'roles'],
    useTenantDestLabel: (tenantId) => organizationName(useTenantLabelResolver(tenantId), tenantId),
    useLabels: (tenantId) => useTenantLabelResolver(tenantId),
    api: platformTenantScopedApi,
    createApiUser: (tenantId, body, opts) => platformApi.createTenantApiUser(tenantId, body, opts),
    queryKeyBase: (tenantId) => ['platform', 'tenants', tenantId],
    renderRowActions: ({ user, scopeRef }) => (
        <PlatformTenantUserRowActions user={user} tenantId={scopeRef} />
    ),
    // Sin página propia: el alta vive en un modal → siempre hay `onDone`, nunca navega.
    usersTable: { showCreatedAt: false },
};

/**
 * Descriptor de auditoría del módulo Platform (`/platform/audit`). NO es tenant-scoped: consulta TODA
 * la auditoría platform. El sentinel `'*'` en `useScopeRef` habilita la query del motor
 * (`enabled: !!scopeRef`) y `queryAudit` lo IGNORA (queda `platformApi.queryAudit(params)`, sin
 * scopeRef). `queryKeyBase` namespacea en `['platform']` → el motor lo cierra con `'audit'`.
 */
export const PLATFORM_AUDIT_DESCRIPTOR: AuditScopeDescriptor = {
    useScopeRef: () => '*',
    queryKeyBase: () => ['platform'],
    api: { queryAudit: (_ref, params) => platformApi.queryAudit(params) },
};
